import { useRef, useState } from 'react';
import { X, ShoppingCart, Minus, Plus, Trash2, Download, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { getImageUrl } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import './CartDrawer.css';

const API = import.meta.env.VITE_API_URL || 'https://octoink-backend.onrender.com';

export default function CartDrawer() {
    const { cartItems, isCartOpen, setIsCartOpen, removeFromCart, updateQuantity, clearCart, totalItems, totalPrice, toast } = useCart();
    const { token } = useAuth();
    const { formatPrice } = useCurrency();
    const navigate = useNavigate();
    const [downloading, setDownloading] = useState(false);

    const handleClose = () => setIsCartOpen(false);

    const handleDownloadAll = async () => {
        if (!token) {
            alert('Please login to download files.');
            navigate('/login');
            return;
        }

        const downloadableItems = cartItems.filter(item => item.hasFile);
        if (downloadableItems.length === 0) {
            alert('No downloadable files in your cart. Use "Order Design" for custom products.');
            return;
        }

        setDownloading(true);
        try {
            if (downloadableItems.length === 1) {
                // Single file — direct download
                await downloadSingle(downloadableItems[0]);
            } else {
                // Multiple files — download individually in sequence
                for (const item of downloadableItems) {
                    await downloadSingle(item);
                    // Short delay to avoid browser blocking multiple downloads
                    await new Promise(r => setTimeout(r, 400));
                }
            }
        } catch (err) {
            console.error('Download error:', err);
            alert(err.message || 'Something went wrong during download.');
        } finally {
            setDownloading(false);
        }
    };

    const downloadSingle = async (item) => {
        const res = await fetch(`${API}/api/products/download/${item._id}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` }
        });

        if (res.status === 403) {
            throw new Error(`"${item.title}" requires purchase before download.`);
        }
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.message || `Download failed for "${item.title}".`);
        }

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${item.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const downloadableCount = cartItems.filter(i => i.hasFile).length;

    return (
        <>
            {/* Backdrop overlay */}
            <div
                className={`cart-overlay ${isCartOpen ? 'open' : ''}`}
                onClick={handleClose}
            />

            {/* Drawer panel */}
            <div className={`cart-drawer ${isCartOpen ? 'open' : ''}`} role="dialog" aria-label="Shopping Cart">
                {/* Header */}
                <div className="cart-drawer-header">
                    <div className="cart-drawer-title">
                        <ShoppingCart size={18} />
                        <span>My Cart</span>
                        {totalItems > 0 && (
                            <span className="cart-count-badge-header">{totalItems}</span>
                        )}
                    </div>
                    <button className="cart-drawer-close" onClick={handleClose} aria-label="Close cart">
                        <X size={18} />
                    </button>
                </div>

                {/* Empty state */}
                {cartItems.length === 0 ? (
                    <div className="cart-empty-state">
                        <div className="cart-empty-icon">
                            <ShoppingBag size={36} />
                        </div>
                        <h3>Your cart is empty</h3>
                        <p>Browse the store and add designs<br />to your cart to get started.</p>
                        <button
                            className="cart-browse-btn"
                            onClick={handleClose}
                        >
                            Browse Designs
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Items list */}
                        <div className="cart-items-list">
                            {cartItems.map(item => (
                                <div key={item._id} className="cart-item">
                                    <div className="cart-item-thumb">
                                        <img src={getImageUrl(item.image)} alt={item.title} />
                                    </div>
                                    <div className="cart-item-info">
                                        <span className="cart-item-category">{item.category}</span>
                                        <p className="cart-item-name" title={item.title}>{item.title}</p>
                                        <span className={`cart-item-price ${!item.price ? 'free' : ''}`}>
                                            {item.price ? formatPrice(item.price * item.quantity) : 'Custom Order'}
                                        </span>
                                        <div className="cart-item-controls">
                                            <button
                                                className="qty-btn"
                                                onClick={() => updateQuantity(item._id, -1)}
                                                aria-label="Decrease quantity"
                                            >
                                                <Minus size={10} />
                                            </button>
                                            <span className="qty-value">{item.quantity}</span>
                                            <button
                                                className="qty-btn"
                                                onClick={() => updateQuantity(item._id, 1)}
                                                aria-label="Increase quantity"
                                            >
                                                <Plus size={10} />
                                            </button>
                                        </div>
                                    </div>
                                    <button
                                        className="cart-item-remove"
                                        onClick={() => removeFromCart(item._id)}
                                        aria-label={`Remove ${item.title}`}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Footer */}
                        <div className="cart-drawer-footer">
                            <div className="cart-summary-row">
                                <span className="cart-summary-label">
                                    {totalItems} item{totalItems !== 1 ? 's' : ''} · {downloadableCount} downloadable
                                </span>
                                <span className="cart-summary-total">{formatPrice(totalPrice)}</span>
                            </div>

                            <button
                                className={`cart-download-all-btn ${downloading ? 'loading' : ''}`}
                                onClick={handleDownloadAll}
                                disabled={downloading || downloadableCount === 0}
                            >
                                <Download size={16} />
                                <span>
                                    {downloading
                                        ? 'Downloading...'
                                        : downloadableCount === 0
                                            ? 'No Downloadable Files'
                                            : `Download All (${downloadableCount})`
                                    }
                                </span>
                            </button>

                            <button className="cart-clear-btn" onClick={clearCart}>
                                Clear cart
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Toast notification */}
            <div className={`cart-toast ${toast ? 'visible' : ''} ${toast?.type === 'info' ? 'info' : ''}`}>
                <span className="cart-toast-dot" />
                {toast?.message}
            </div>
        </>
    );
}
